import { TicketType } from "./enums";
import { TicketItem, TicketItemFormValues } from "./ticket";

export enum InventoryIndexSortKeys {
    quantity = 0,
    unitPrice = 1,
    name = 2,
    ticketItemsAppliedTo = 3,
}

export enum InventoryIndexVendorSortKeys {
    createdDate = 0,
    defaultMarkup = 1,
    name = 2,
}

export enum InventoryIndexOrders {
    ascending = 0,
    descending = 1,
}

export interface AddPartToStockDto {
    partId?: string;
    partNumber: string;
    description: string;
    locationId?: string;
    location?: string;
    vendorId?: string;
    quantity: number;
    reOrderLevel: number;
    cost: number;
    markup: number;
    price: number;
    notes?: string;
    lowInventoryReminderEnabled?: boolean;
    coreTrackingEnabled?: boolean;
    coreTrackingId?: string;
    coreValue?: number;
    returned?: boolean;
    refunded?: boolean;
}

export interface UpdatePartToStockDto {
    partId?: string;
    partNumber: string;
    description: string;
    locationId?: string;
    location?: string;
    vendorId?: string;
    quantity: number;
    reOrderLevel: number;
    cost: number;
    markup: number;
    price: number;
    notes?: string;
    lowInventoryReminderEnabled?: boolean;
    coreTrackingEnabled?: boolean;
    coreTrackingId?: string;
    coreValue?: number;
    returned?: boolean;
    refunded?: boolean;
}

export interface UpdatePartNotificationSettingsDto {
    reOrderLevel: number;
}
export interface NewLocationFormValues {
    location: string;
}

export interface NewVendorFormValues {
    vendor: string;
}

export class NewLocationFormValues implements NewLocationFormValues {
    location: string = "";
    constructor(init?: NewLocationFormValues) {
        if (init) {
            this.location = init.location ?? "";
        }
    }
}

export class NewVendorFormValues implements NewVendorFormValues {
    location: string = "";
    constructor(init?: NewVendorFormValues) {
        if (init) {
            this.vendor = init.vendor ?? "";
        }
    }
}

export interface UpdatePartNotificationSettingsFormValues {
    reOrderLevel: number;
}

export class UpdatePartNotificationSettingsFormValues implements UpdatePartNotificationSettingsFormValues {
    reOrderLevel: number = 0;
    constructor(init: InventoryStockPartDetailsDto) {
        if (init) {
            this.reOrderLevel = init.reOrderLevel ?? 0;
        }
    }
}

export interface GetVendorForInventoryItemRequestDto {
    inventoryIndexId: string;
    vendorId: string;
    totalQuantity: number;
    cost: number;
}

export interface InventoryIndexDto {
    partId: string;
    inventoryStockId: string;
    partNumber: string;
    vendorId?: string;
    vendorName?: string;
    vendorMarkup?: number;
    markup: number;
    name: string;
    description?: string;
    notes?: string;
    totalQuantity?: number;
    quantityInStock?: number;
    reOrderLevel?: number;
    cost: number;
    unitPrice: number;
    core?: boolean;
    location?: string;
    locationId?: string;
    coreTrackingEnabled?: boolean;
    coreTrackingId?: string;
    coreValue?: number;
    returned?: boolean;
    refunded?: boolean;
}

export interface InventoryStockPartDetailsDto {
    id: string;
    partNumber: string;
    partName: string;
    markup?: number;
    vendorId?: string;
    vendorName?: string;
    vendorMarkup?: number;
    reOrderLevel?: number;
    averageCost?: number;
    unitPrice?: number;
    totalQuantity?: number;
    quantityInStock?: number;
    coreTrackingEnabled?: boolean;
    lowInventoryReminderEnabled?: boolean;
    coreValue?: number;
    returned?: boolean;
    refunded?: boolean;
    location: string;
}

export interface InventoryStockPartDetailsLocationDto {
    id: string;
    name: string;
    quantityInStock: number;
    notes: string;
}

export interface InventoryStockPartDetailsTicketDto {
    ticketId: string;
    ticketNumber?: number;
    customerName: string;
    vehicleMake: string;
    vehicleModel: string;
    vehicleYear: number;
    vehicleId: string;
    ticketTotal: number;
    quantity: number;
}

export interface InventoryStockPartDetailsVendorDto {
    id: string;
    name: string;
    cost: number;
    markup: number;
    quantityInStock: number;
    quantityUsed: number;
    coreTracking: boolean;
    coreValue?: number;
}

export interface InventoryTicketDto {
    id: string;
    createdDate: Date;
    sumOfTicketsItemsUsingPart?: number;
    ticketItemsThatUsesPart: TicketItem[];
    estimateNumber?: number;
    invoiceNumber?: number;
    poNumber: string;
    description: string;
    notes: string;
    subtotal: number;
    taxes: number;
    total: number;
}

export interface SearchInventoryIndexDto {
    partNumber?: string;
    partCode?: string;
    vendorId?: string;
    locationId?: string;
    sortKey?: InventoryIndexSortKeys;
    order?: InventoryIndexOrders;
}

export interface TicketForVendorInventoryItemDto {
    ticketId: string;
    ticketType: TicketType;
    ticketNumber?: number;
    date?: Date;
    customerInformation: string;
    customerId?: string;
}

export interface VendorForInventoryIndexDto {
    inventoryIndexId: string;
    vendorId: string;
    vendorName: string;
    cost: number;
    markup: number;
    quantityInStock: number;
    coreTracking: boolean;
}

export interface PartInventoryFormValues extends TicketItemFormValues {
    location?: string | undefined;
    locationId?: string | undefined;
    reOrderLevel?: number | undefined;
    notes?: string | undefined;
    lowInventoryReminderEnabled?: boolean | undefined;
}

export class PartInventoryFormValues extends TicketItemFormValues {
    location?: string | undefined = undefined;
    locationId?: string | undefined = undefined;
    reOrderLevel?: number | undefined = undefined;
    notes?: string | undefined = undefined;
    lowInventoryReminderEnabled?: boolean | undefined = undefined;
    constructor(init?: PartInventoryFormValues | undefined) {
        super(init);
        if (init) {
            this.location = init.location;
            this.locationId = init.locationId;
            this.reOrderLevel = init.reOrderLevel;
            this.notes = init.notes;
            this.lowInventoryReminderEnabled = init.lowInventoryReminderEnabled;
        }
    }
}

export interface SavePurchasesDto {
    poNumber: string;
    purchasesToSave: SavePurchaseFormValues[];
}

export interface SavePurchaseFormValues extends PartInventoryFormValues {
    id?: string | undefined;
    partId?: string | undefined;
    partNumber?: string | undefined;
    locationId?: string | undefined;
    unitCost?: number | undefined;
    quantityPurchased?: number | undefined;
    isDirty?: boolean;
}

export class SavePurchaseFormValues extends PartInventoryFormValues  {
    id?: string | undefined = undefined;
    partId?: string | undefined = undefined;
    partNumber?: string | undefined = undefined;
    locationId?: string | undefined = undefined;
    unitCost?: number | undefined = undefined;
    quantityPurchased?: number | undefined = undefined;
    isDirty?: boolean | undefined = false;
    constructor(init?: SavePurchaseFormValues) {
        super(init);
        if (init) {
            this.id = init.id;
            this.partNumber = init.partNumber;
            this.locationId = init.locationId;
            this.unitCost = init.unitCost;
            this.vendorId = init.vendorId;
            this.quantityPurchased = init.quantityPurchased;
        }
        this.isDirty = false;
    }
}

export interface UsedQuantityDto {
    quantity: number;
}

export interface PurchaseHistoryDto {
    id: string;
    purchaseDate: Date;
    poNumber: string;
    vendor: string;
    quantityPurchased: number;
    cost: number;
    totalCost: number;
}

export interface PurchasesItemDto {
    id: string;
    partNumber: string;
    partDescription: string;
    unitCost?: number;
    quantityPurchased?: number;
    locationId: string | undefined;
    location: string | undefined;
    vendorId: string | undefined;
}

export interface InventoryStockPartDetailsCoresDto {
    id: string;
    quantity?: number;
    coreValue?: number;
    returned?: boolean;
    returnedQuantity: number;
    refunded?: boolean;
    refundedQuantity: number;
}

export interface InventoryStockCountsDto {
    id: string;
    partNumber: string,
    partName: string,
    quantityInStock: number,
    totalQuantity?: number,
}


export interface CoreTrackingFormValues {
    id?: string | undefined;
    totalQty?: number | undefined;
    returned?: boolean | undefined;
    refunded?: boolean | undefined;
    isDirty: boolean;
}

export class CoreTrackingFormValues implements CoreTrackingFormValues {
    id?: string | undefined;
    totalQty?: number | undefined;
    returned?: boolean | undefined;
    refunded?: boolean | undefined;
    isDirty = false;
    constructor(init?: CoreTrackingFormValues) {
        if (init) {
            this.id = init.id;
            this.totalQty = init.totalQty;
            this.returned = init.returned;
            this.refunded = init.refunded;
            this.isDirty = false;
        }
    }
}